/**
 * social_context.ts — the client's read of the player-to-player rows
 * (2026-09-18): my party and its members, invitations, intents, messages,
 * pings, friends, blocks, recent teammates, my open trade — every row here
 * arrived through the server's row-level security, so "what I can see" IS
 * "what I am allowed to see". Mutes are a LOCAL preference (localStorage):
 * they hide, they never inform the other side.
 */
import { atlas } from '../state.js';

interface IdLike { toHexString(): string; isEqual(o: unknown): boolean }
interface TsLike { microsSinceUnixEpoch: bigint }
export interface PartyRow { partyId: bigint; leader: IdLike; rosterRev: number; state: string }
export interface MemberRow { memberId: bigint; partyId: bigint; identity: IdLike; ready: boolean; joinedAt: TsLike }
export interface InviteRow { inviteId: bigint; partyId: bigint; inviter: IdLike; invitee: IdLike; expiresAt: TsLike }
export interface IntentRow { intentId: bigint; partyId: bigint; identity: IdLike; gameId: bigint; rosterRev: number; state: string; expiresAt: TsLike }
export interface PartyMsgRow { msgId: bigint; partyId: bigint; sender: IdLike; senderName: string; kind: string; text: string; phraseKey: string; at: TsLike }
export interface PingRow { pingId: bigint; partyId: bigint; sender: IdLike; kind: string; worldId: bigint; x: number; y: number; z: number; targetKey: string; parentId: bigint; expiresAt: TsLike; at: TsLike }
export interface EdgeRow { edgeId: bigint; a: IdLike; b: IdLike; state: string; at: TsLike }
export interface BlockRow { blockId: bigint; blocker: IdLike; blocked: IdLike }
export interface HistoryRow { histId: bigint; identity: IdLike; other: IdLike; otherName: string; partyId: bigint; at: TsLike }
export interface DmRow { msgId: bigint; sender: IdLike; senderName: string; target: IdLike; text: string; nonce: number; at: TsLike }
export interface TradeRow { tradeId: bigint; a: IdLike; b: IdLike; state: string; rev: number; offerA: string; offerB: string; sovA: bigint; sovB: bigint; confirmA: number; confirmB: number; result: string; expiresAt: TsLike }

type Db = Record<string, { iter(): Iterable<unknown> } | undefined>;
export function rows<T>(name: string): T[] {
  try { const t = (atlas.conn?.db as unknown as Db)[name]; return t ? ([...t.iter()] as T[]) : []; } catch { return []; }
}
const me = (): string | null => atlas.identityHex;
const isMe = (id: IdLike): boolean => { const m = me(); return m !== null && id.toHexString() === m; };
export const usOf = (t: TsLike): number => Number(t.microsSinceUnixEpoch / 1000n);

export function myParty(): { party: PartyRow; member: MemberRow } | null {
  for (const m of rows<MemberRow>('partyMember')) {
    if (!isMe(m.identity)) continue;
    const p = rows<PartyRow>('party').find((x) => x.partyId === m.partyId && x.state === 'open');
    if (p) return { party: p, member: m };
  }
  return null;
}
export const partyMembers = (partyId: bigint): MemberRow[] => rows<MemberRow>('partyMember').filter((m) => m.partyId === partyId);
export const myInvites = (): InviteRow[] => rows<InviteRow>('partyInvite').filter((i) => isMe(i.invitee) && usOf(i.expiresAt) > Date.now());
export const sentInvites = (): InviteRow[] => rows<InviteRow>('partyInvite').filter((i) => isMe(i.inviter));
export const partyIntents = (partyId: bigint): IntentRow[] => rows<IntentRow>('partyQueueIntent').filter((i) => i.partyId === partyId && i.state === 'open' && usOf(i.expiresAt) > Date.now());
/** Retention is enforced on READ as well as on write: a dormant party or
 *  conversation keeps rows until its next action sweeps them, and the promise
 *  is about what a reader sees (the blueprint's "reads enforce expiry"). */
export const DM_KEEP_MS = 7 * 24 * 3600_000;
export const PARTY_MSG_KEEP_MS = 24 * 3600_000;
export const partyMessages = (partyId: bigint): PartyMsgRow[] => rows<PartyMsgRow>('partyMessage')
  .filter((m) => m.partyId === partyId && Date.now() - usOf(m.at) <= PARTY_MSG_KEEP_MS)
  .sort((x, y) => (x.msgId < y.msgId ? -1 : 1));
export const livePings = (partyId: bigint): PingRow[] => rows<PingRow>('partyPing').filter((p) => p.partyId === partyId && usOf(p.expiresAt) > Date.now());
export const friends = (): Array<{ hex: string; state: string; incoming: boolean }> => rows<EdgeRow>('contactEdge')
  .filter((e) => isMe(e.a) || isMe(e.b))
  .map((e) => ({ hex: (isMe(e.a) ? e.b : e.a).toHexString(), state: e.state, incoming: e.state === 'pending' && isMe(e.b) }));
export const blocks = (): string[] => rows<BlockRow>('contactBlock').filter((b) => isMe(b.blocker)).map((b) => b.blocked.toHexString());
export const recentTeammates = (): HistoryRow[] => {
  const seen = new Map<string, HistoryRow>();
  for (const h of rows<HistoryRow>('partyHistory').filter((h) => isMe(h.identity)).sort((x, y) => (x.histId < y.histId ? 1 : -1))) {
    const k = h.other.toHexString();
    if (!seen.has(k)) seen.set(k, h);
  }
  return [...seen.values()];
};
export const directMessages = (): DmRow[] => rows<DmRow>('directMessage')
  .filter((m) => Date.now() - usOf(m.at) <= DM_KEEP_MS)
  .sort((x, y) => (x.msgId < y.msgId ? -1 : 1));
export const myTrade = (): TradeRow | null => rows<TradeRow>('trade').find((t) => (isMe(t.a) || isMe(t.b)) && (t.state === 'proposed' || t.state === 'open')) ?? null;

/** Names: the account table (subscribed by the PvP module) → display name. */
export function nameOfHex(hex: string): string {
  for (const a of rows<{ identity: IdLike; username: string; displayName: string }>('account')) if (a.identity.toHexString() === hex) return a.displayName || a.username;
  return hex.slice(0, 8);
}
export function usernameOfHex(hex: string): string | null {
  for (const a of rows<{ identity: IdLike; username: string }>('account')) if (a.identity.toHexString() === hex) return a.username;
  return null;
}

// ── Mutes: a local preference ───────────────────────────────────────────
const MUTE_KEY = 'atlas:muted';
let muteCache: Set<string> | null = null;
export function muted(): Set<string> {
  if (muteCache) return muteCache;
  try { muteCache = new Set(JSON.parse(localStorage.getItem(MUTE_KEY) ?? '[]') as string[]); } catch { muteCache = new Set(); }
  return muteCache;
}
export function setMuted(hex: string, on: boolean): void {
  const m = muted();
  if (on) m.add(hex); else m.delete(hex);
  try { localStorage.setItem(MUTE_KEY, JSON.stringify([...m])); } catch { /* private mode */ }
}
export const isMuted = (hex: string): boolean => muted().has(hex);
/** Hidden from me: muted (my local preference) or blocked (the server boundary).
 *  A block refuses DIRECTED contact; shared spaces — party text, pings, local
 *  say — still carry their rows, so the reader hides them here. */
export const hiddenFrom = (hex: string): boolean => muted().has(hex) || blocks().includes(hex);

/** The quick phrases: a key on the wire, a sentence in the reader's language. */
export const PHRASES: Record<string, string> = {
  ready: 'Ready', not_ready: 'Not ready', wait: 'Wait', follow: 'Follow me', help: 'Help!', thanks: 'Thank you', sorry: 'Sorry', gg: 'Good game',
  need_seat: 'Need a seat', need_repairs: 'Need repairs', offering_transport: 'Offering transport', learning: 'Learning — go easy', no_voice: 'No voice, text only',
  leaving_soon: 'Leaving soon', on_my_way: 'On my way', i_can: 'I can do that', cannot: 'Cannot',
};
export const PING_LABEL: Record<string, string> = {
  go: 'Go here', regroup: 'Regroup', wait: 'Wait', avoid: 'Avoid', threat: 'Threat', help: 'Help needed', resource: 'Resource', interact: 'Interact',
  work: 'Work here', objective: 'Objective', meet: 'Meet here', travel: 'Travel proposal', ack: 'Ack',
};

// ── The ambiguous answers ───────────────────────────────────────────────
/** `/accept`, `/decline`, `/ready` and `/leave` mean one thing in a duel or an
 *  arena match and another in a party. PURE so the harness can exercise every
 *  combination: a shared `case` list in the chat silently swallowed every
 *  party answer (a party invitation could never be accepted, 2026-09-18). */
export type AnswerCmd = 'accept' | 'decline' | 'ready' | 'leave';
export interface AnswerContext {
  duelInvite: boolean;      // a duel challenge is waiting
  duelState: string | null; // my live duel's state
  matchState: string | null;// my live arena match's state
  inMatch: boolean;
  queued: boolean;
  partyInvites: number;
  inParty: boolean;
}
/** Which module should answer? A live PvP answer wins; else a live party
 *  answer; else PvP (its empty message is the friendlier one). */
export function routeAnswer(cmd: AnswerCmd, x: AnswerContext): 'pvp' | 'social' {
  const pvpWants = (cmd === 'accept' || cmd === 'decline') ? x.duelInvite
    : cmd === 'ready' ? (x.duelState === 'staging' || x.matchState === 'staging' || x.matchState === 'between')
      : x.inMatch || x.queued;
  const socialWants = (cmd === 'accept' || cmd === 'decline') ? x.partyInvites > 0 : x.inParty;
  return !pvpWants && socialWants ? 'social' : 'pvp';
}
